/**
 * 75:2's StatGrid: the four KPI cards (`api`'s `reading/kpis` route,
 * `AccountKpisOut`).
 *
 * The frame draws a "+12%" comparison chip on the reach card and an "IC 95%"
 * confidence interval under the sentiment card; `AccountKpisOut` carries
 * neither a comparison window nor an interval (no `CompareWindowDep` on this
 * route, no stddev in `CommentSentimentAggregate`), so both are left off
 * rather than fabricated -- same shape as `MonitoringOverview`'s own
 * documented gaps.
 *
 * The frame's third card reads "38 vezes / compartilhamentos de publicações
 * da campanha" (an amplification count); the field it renders,
 * `related_share`, is a 0..1 fraction of the account's own output that
 * counted as related (`align-fix-accounts-api-reading-routes`'s own report
 * maps this exact field to this exact card slot). No producing field counts
 * amplification events, so the value renders as the percentage it actually
 * is, with a caption naming what it is rather than the frame's uncovered
 * wording.
 */

import type { ReactNode } from 'react';

import { Absent } from '../../../../chrome/measure';
import { score } from '../../../../chrome/measure/format';
import { StatCard, StatGrid } from '../../../../chrome/page';
import { QueryStates } from '../../../../chrome/state/QueryStates';
import { getLocale } from '../../../../i18n';
import { compact, percent } from '../../../../i18n/format';
import { Skeleton } from '../../../../ui/skeleton';
import { useAccountKpis, type ReadingWindow } from '../api/queries';
import type { AccountKpis } from '../api/schemas';
import { am, amPlural } from '../messages';

function isEmptyKpis(data: AccountKpis): boolean {
  return (
    data.reach === null &&
    data.publications === null &&
    data.related_share === null &&
    data.sentiment === null
  );
}

export function KpiRowSkeleton(): ReactNode {
  return (
    <StatGrid>
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          aria-hidden
          className="flex flex-col gap-2 rounded-lg border border-border p-4"
        >
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-3 w-36" />
        </div>
      ))}
    </StatGrid>
  );
}

function KpiValues(props: { data: AccountKpis }): ReactNode {
  const locale = getLocale();
  const { data } = props;
  const total =
    data.publications !== null && data.related_share !== null && data.related_share > 0
      ? Math.round(data.publications / data.related_share)
      : null;

  return (
    <StatGrid>
      <StatCard
        label={am('kpi.reach.label')}
        value={data.reach !== null ? compact(data.reach, locale) : <Absent reason="not_measured" />}
        caption={data.reach !== null ? am('kpi.reach.caption') : undefined}
      />
      <StatCard
        label={am('kpi.publications.label')}
        value={
          data.publications !== null ? (
            compact(data.publications, locale)
          ) : (
            <Absent reason="not_measured" />
          )
        }
        caption={
          total !== null
            ? amPlural('kpi.publications.caption.other', total, { total: compact(total, locale) })
            : undefined
        }
      />
      <StatCard
        label={am('kpi.relatedShare.label')}
        value={
          data.related_share !== null ? (
            percent(data.related_share, locale)
          ) : (
            <Absent reason="not_measured" />
          )
        }
        caption={data.related_share !== null ? am('kpi.relatedShare.caption') : undefined}
      />
      <StatCard
        label={am('kpi.sentiment.label')}
        value={
          data.sentiment !== null ? (
            score(data.sentiment * 100, locale)
          ) : (
            <Absent reason="not_measured" />
          )
        }
      />
    </StatGrid>
  );
}

export function KpiRow(props: {
  campaignId: string;
  trackedAccountId: string;
  window: ReadingWindow;
}): ReactNode {
  const query = useAccountKpis(props.campaignId, props.trackedAccountId, props.window);

  return (
    <QueryStates
      query={query}
      isEmpty={isEmptyKpis}
      loading={<KpiRowSkeleton />}
      empty={
        <StatGrid>
          {(
            [
              am('kpi.reach.label'),
              am('kpi.publications.label'),
              am('kpi.relatedShare.label'),
              am('kpi.sentiment.label'),
            ] as const
          ).map((label) => (
            <StatCard key={label} label={label} value={<Absent reason="not_measured" />} />
          ))}
        </StatGrid>
      }
    >
      {(data) => <KpiValues data={data} />}
    </QueryStates>
  );
}
